import React, { useState, useRef, useEffect } from "react";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import Popover from "@mui/material/Popover";
import Popper from "@mui/material/Popper";
import Paper from "@mui/material/Paper";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useLazyQuery } from "@apollo/client/react";
import { usePopupState, bindTrigger, bindPopover } from "material-ui-popup-state/hooks";
import { MASTER_SEARCH_QUERY } from "src/constants/graphql/queries";
import debounce from "lodash/debounce";
import SearchResults from "./SearchResults";
import useAuth from "../../../../hooks/useAuth";
import ScanditScanner from "../../../../components/barcode/ScanditScanner/ScanditScanner";

const styles = {
  popoverPaper: {
    width: "400px",
    maxHeight: "500px",
    mt: 2,
    borderRadius: "20px",
    overflow: "hidden",
  },
  popperPaper: {
    width: "400px",
    maxHeight: "500px",
    borderRadius: "20px",
    overflow: "hidden",
    marginTop: "10px",
    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
  },
  stickyHeader: {
    position: "sticky",
    top: 0,
    zIndex: 1,
    backgroundColor: "background.paper",
    borderBottom: "1px solid",
    borderColor: "divider",
  },
  textField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: "28px",
      // Add these properties to control height
      height: "44px", // Adjust this value to your desired height
      "& .MuiOutlinedInput-input": {
        padding: "8px 14px", // Adjust padding to vertically center the text
      },
    },
  },
  desktopTextField: {
    width: "200px",
    "& .MuiOutlinedInput-root": {
      borderRadius: "28px",
      height: "44px",
      backgroundColor: "background.paper",
      "& .MuiOutlinedInput-input": {
        padding: "8px 14px",
      },
    },
  },
  resultsContainer: {
    maxHeight: "450px",
    overflow: "auto",
  },
};

export default function Search() {
  const { user } = useAuth();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [allResults, setAllResults] = useState([]);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(false);
  const searchInputRef = useRef(null);
  const popupState = usePopupState({ variant: "popover", popupId: "searchPopover" });
  const [search, { loading, data }] = useLazyQuery(MASTER_SEARCH_QUERY, {
    fetchPolicy: "network-only",
  });
  const metadata = data?.masterSearchCollection?.metadata || {};
  const hasMore = metadata.totalPages ? page < metadata.totalPages : false;

  const debouncedSearch = useRef(
    debounce(term => {
      if (term.trim().length > 0) {
        search({ variables: { search: term, limit, page: 1 } });
      }
    }, 1000)
  ).current;

  const executeSearch = (term = searchTerm) => {
    if (term.trim().length > 0) {
      search({ variables: { search: term, limit, page: 1 } });
      if (isDesktop) {
        setDesktopOpen(true);
      }
    }
  };

  const handleSearchChange = event => {
    const value = event.target.value;
    setSearchTerm(value);
  };

  const handleKeyDown = event => {
    if (event.key === "Enter") {
      executeSearch();
    }
  };

  const handleDesktopFocus = () => {
    if (searchTerm.length > 0) {
      setDesktopOpen(true);
    }
  };

  const handleDesktopClose = () => {
    setDesktopOpen(false);
  };

  const loadMore = () => {
    if (!loading && hasMore && searchTerm.trim().length > 0) {
      const nextPage = page + 1;
      setPage(nextPage);
      search({ variables: { search: searchTerm, limit, page: nextPage } });
    }
  };

  const handleScannerToggle = () => {
    setScannerOpen(prev => !prev);
  };

  const handleScannerClose = () => {
    setScannerOpen(false);
  };

  const handleScanComplete = barcode => {
    setSearchTerm(barcode);
    executeSearch(barcode);
  };

  useEffect(() => {
    setAllResults([]);
    setPage(1);
  }, [searchTerm]);

  useEffect(() => {
    if (data?.masterSearchCollection?.collection) {
      const newResults = data.masterSearchCollection.collection;
      if (page === 1) {
        setAllResults(newResults);
      } else {
        setAllResults(prev => [...prev, ...newResults]);
      }
    }
  }, [data, page]);

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  if (isDesktop) {
    return (
      <>
        <ClickAwayListener onClickAway={handleDesktopClose}>
          <Box>
            <TextField
              fullWidth
              inputRef={searchInputRef}
              placeholder="Search..."
              value={searchTerm}
              onChange={handleSearchChange}
              onFocus={handleDesktopFocus}
              onKeyDown={handleKeyDown}
              variant="outlined"
              sx={styles.desktopTextField}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {searchTerm && (
                      <IconButton size="small" onClick={() => {
                        setSearchTerm("");
                        setDesktopOpen(false);
                      }}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                    <Tooltip arrow title="Scan Barcode">
                      <IconButton size="small" onClick={handleScannerToggle}>
                        <QrCodeScannerIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
              autoComplete="off"
            />
            <Popper
              open={desktopOpen && searchTerm.length > 0}
              anchorEl={searchInputRef.current}
              placement="bottom-start"
              style={{ zIndex: 1300 }}
            >
              <Paper sx={styles.popperPaper}>
                <Box sx={styles.resultsContainer}>
                  <SearchResults
                    loading={loading}
                    searchTerm={searchTerm}
                    searchResults={allResults}
                    hasMore={hasMore}
                    loadMore={loadMore}
                    metadata={metadata}
                    onClose={handleDesktopClose}
                  />
                </Box>
              </Paper>
            </Popper>
          </Box>
        </ClickAwayListener>
        {scannerOpen && (
          <ScanditScanner
            onDetected={handleScanComplete}
            onClose={handleScannerClose}
          />
        )}
      </>
    );
  }

  return (
    <>
      <Tooltip title="Search">
        <IconButton {...bindTrigger(popupState)}>
          <SearchIcon />
        </IconButton>
      </Tooltip>
      <Popover
        {...bindPopover(popupState)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        PaperProps={{
          sx: styles.popoverPaper,
        }}
      >
        <Box>
          <Box sx={styles.stickyHeader}>
            <TextField
              autoFocus
              fullWidth
              inputRef={searchInputRef}
              placeholder="What can I help you find?"
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyDown={handleKeyDown}
              variant="outlined"
              sx={styles.textField}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {searchTerm && (
                      <IconButton size="small" onClick={() => setSearchTerm("")}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                    <Tooltip arrow title="Scan Barcode">
                      <IconButton size="small" onClick={handleScannerToggle}>
                        <QrCodeScannerIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
              autoComplete="off"
            />
          </Box>
          <Box sx={styles.resultsContainer}>
            <SearchResults
              loading={loading}
              searchTerm={searchTerm}
              searchResults={allResults}
              hasMore={hasMore}
              loadMore={loadMore}
              metadata={metadata}
              onClose={popupState.close}
            />
          </Box>
        </Box>
      </Popover>
      {scannerOpen && (
        <ScanditScanner
          onDetected={handleScanComplete}
          onClose={handleScannerClose}
        />
      )}
    </>
  );
}
